<template>
    <span>
        <template v-if="prescription && prescription.rxNumber">
            <b-button v-show="showRefillAuthRequest" v-b-modal="refillAuthModalId"
                :disabled="faxActionDisabled || patient?.deceased" :variant="variant" :size="size">
                Refill Authorization
            </b-button>
            <b-dropdown v-if="hasContactPrescriberAccess" text="Contact Prescriber" :variant="variant" :size="size"
                right>
                <b-dropdown-item v-if="hasRxClarificationAccess" v-b-modal="rxClarificationModalId"
                    :disabled="faxActionDisabled">
                    RX Clarification
                </b-dropdown-item>
                <b-dropdown-item v-if="hasAlternateRxAccess" v-b-modal="alternateRxModalId"
                    :disabled="faxActionDisabled">
                    Request Alternate Medication
                </b-dropdown-item>
                <b-dropdown-item v-if="hasProviderCommunicationsSendPermission"
                    @click="openProviderCommunicationModal"
                    :disabled="!prescription || !doctor || !doctor.npiNumber || !patient || !patient.id">
                    Provider Communication
                </b-dropdown-item>
                <b-dropdown-divider v-if="filteredCustomContactPrescriptionTemplates.length" />
                <b-dropdown-item v-for="template in filteredCustomContactPrescriptionTemplates"
                    :key="template.id"
                    @click="customContactPrescriberTemplate = template; $bvModal.show(customPrescriberModalId)"
                    :disabled="faxActionDisabled">
                    {{ template.name }}
                </b-dropdown-item>
            </b-dropdown>
        </template>

        <FaxToPhysicianFormModal :drug="drug" :patient="patient" :prescriber="doctor" :prescription="prescription"
            :store="store" :modal-id="refillAuthModalId" @saved="$emit('saved')" />

        <FaxToPhysicianFormModal :drug="drug" :form-type="faxFormTypeRequestEnum.RxClarification"
            :patient="patient" :prescriber="doctor" :prescription="prescription" :store="store"
            :modal-id="rxClarificationModalId" @saved="$emit('saved')" />

        <AlternateRxFormModal :drug="drug" :form-type="faxFormTypeRequestEnum.AlternateRxRequest"
            :patient="patient" :prescriber="doctor" :prescription="prescription" :store="store"
            :modal-id="alternateRxModalId" @saved="$emit('saved')" />

        <FaxToPhysicianFormModal :drug="drug" :custom-template="customContactPrescriberTemplate"
            :patient="patient" :prescriber="doctor" :prescription="prescription" :store="store"
            :modal-id="customPrescriberModalId" @saved="$emit('saved')" />

        <ProviderCommunicationModal v-if="providerCommunicationModalVisible && patient && providerCommunicationContext"
            :thread="null"
            :initiate-context="providerCommunicationContext"
            :patient-id="+patient.id"
            @close="closeProviderCommunicationModal" />
    </span>
</template>

<script lang="ts">
// Contact-prescriber action cluster for a prescription: the Refill Authorization button, the
// Contact Prescriber dropdown (RX Clarification / Alternate Medication / Provider Communication /
// custom fax templates), and the fax + provider-communication modals they open.
//
// Contract: read-only entity props in, a single `saved` event out (bubbled from the modals).
// Permission flags arrive as props; this component never reads $permissions. The modals stay
// mounted regardless of the current prescription so an in-progress form is not discarded when the
// host resets the prescription -- only the trigger buttons are gated on the active prescription.
import { Component, Vue, Prop } from "vue-property-decorator";
import Axios from "axios";
import { Patient } from "@/models/Patient";
import { Prescription } from "@/models/Prescription";
import { Prescriber } from "@/models/Prescriber";
import { Program } from "@/models/Program";
import { Store } from "@/models/Store";
import { Drug } from "@/models/Drug/Drug";
import { PrescriberContactTemplate } from "@/models/FaxTemplates";
import { FaxType as FaxToPhysicianFormType } from "@/models/PrescriberFollowUpCase";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import FaxToPhysicianFormModal from "@/components/Prescription/FaxToPhysicianFormModal.vue";
import AlternateRxFormModal from "@/components/Prescription/AlternateRxFormModal.vue";
import ProviderCommunicationModal, { InitiateContext } from "@/components/ProviderCommunications/ProviderCommunicationModal.vue";

@Component({
    components: {
        FaxToPhysicianFormModal,
        AlternateRxFormModal,
        ProviderCommunicationModal,
    },
})
export default class ContactPrescriberActions extends Vue {
    @Prop({ required: true }) patient!: Patient;
    @Prop({ required: true }) prescription!: Prescription;
    @Prop({ required: true }) doctor!: Prescriber;
    @Prop({ required: true }) program!: Program;
    @Prop({ required: true }) store!: Store;
    @Prop({ required: true }) drug!: Drug;

    // Permission flags are supplied by the host; this component never reads $permissions itself.
    @Prop({ default: false }) hasContactPrescriberAccess!: boolean;
    @Prop({ default: false }) hasRxClarificationAccess!: boolean;
    @Prop({ default: false }) hasAlternateRxAccess!: boolean;
    @Prop({ default: false }) hasProviderCommunicationsSendPermission!: boolean;

    // Shows the Refill Authorization button when the next fill cannot be covered.
    @Prop({ default: false }) showRefillAuthRequest!: boolean;

    // Reuse hooks: idPrefix keeps modal ids unique when this cluster is mounted on more than one
    // page, and variant/size let the host match its button styling. Defaults reproduce the
    // prescription details view unchanged.
    @Prop({ default: "" }) idPrefix!: string;
    @Prop({ default: "info" }) variant!: string;
    @Prop({ default: "" }) size!: string;

    faxFormTypeRequestEnum = FaxToPhysicianFormType;

    providerCommunicationModalVisible = false;
    providerCommunicationContext: InitiateContext | null = null;

    protected customContactPrescriberTemplates: PrescriberContactTemplate[] = [];
    private customContactPrescriberTemplate: PrescriberContactTemplate | null = null;

    created() {
        this.getPrescriberCustomContactTemplates();
    }

    get refillAuthModalId(): string {
        return `${this.idPrefix}request-refill-auth-fax`;
    }

    get rxClarificationModalId(): string {
        return `${this.idPrefix}request-rx-clarification-fax`;
    }

    get alternateRxModalId(): string {
        return `${this.idPrefix}request-alternate-rx-fax`;
    }

    get customPrescriberModalId(): string {
        return `${this.idPrefix}custom-prescriber-fax`;
    }

    get faxActionDisabled(): boolean {
        return !(this.prescription && this.prescription.rxNumber && this.prescription.rfNumber != null);
    }

    get filteredCustomContactPrescriptionTemplates(): PrescriberContactTemplate[] {
        return this.customContactPrescriberTemplates.filter(t => {
            if (!t.programs.length) {
                return true;
            }
            return t.programs.find(p => p.id === this.program?.id);
        });
    }

    async getPrescriberCustomContactTemplates() {
        try {
            const resp = await Axios.get("/Prescriber/contact-templates/true");
            this.customContactPrescriberTemplates = resp.data;
        } catch (err) {
            // Keep any previously loaded templates rather than clearing the menu, and surface the
            // failure instead of swallowing it silently.
            console.error(err);
            (this as any).$notification(NotificationOptions.error(err));
        }
    }

    openProviderCommunicationModal() {
        if (!this.doctor?.npiNumber || !this.patient?.id) {
            return;
        }
        this.providerCommunicationContext = {
            prescriberNPI: this.doctor.npiNumber,
            prescriberFirstName: this.doctor.firstName ?? null,
            prescriberLastName: this.doctor.lastName ?? null,
            storeID: this.prescription?.storeID ?? null,
            rxNumber: this.prescription?.rxNumber ?? null,
            rfNumber: this.prescription?.rfNumber ?? null,
        };
        this.providerCommunicationModalVisible = true;
    }

    closeProviderCommunicationModal() {
        this.providerCommunicationModalVisible = false;
        this.providerCommunicationContext = null;
    }
}
</script>